import { Paths } from "expo-file-system";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Alert, Linking, Platform } from "react-native";
import { captureException } from "@utils/captureException";

/** Warn when free space is below this many bytes (500 MB). */
export const LOW_STORAGE_BYTES_THRESHOLD = 500 * 1024 * 1024;

/** Warn when free/total ratio is below this fraction (2%). */
export const LOW_STORAGE_RATIO_THRESHOLD = 0.02;

/** Don't re-prompt within this window after a dismiss (24 hours in ms). */
export const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000;

export const LAST_DISMISSED_KEY = "@lissen/low_storage_dismissed_at";

/**
 * Returns true when the device storage is critically low.
 * Pure function — suitable for unit testing.
 */
export function isStorageCriticallyLow(
  availableBytes: number,
  totalBytes: number,
): boolean {
  if (availableBytes < LOW_STORAGE_BYTES_THRESHOLD) {
    return true;
  }
  if (totalBytes > 0 && availableBytes / totalBytes < LOW_STORAGE_RATIO_THRESHOLD) {
    return true;
  }
  return false;
}

/**
 * Returns true when the cooldown has elapsed since the last dismissal,
 * meaning we should show the prompt again.
 * Pure function — suitable for unit testing.
 */
export function isCooldownElapsed(
  lastDismissedAt: number | null,
  nowMs: number,
): boolean {
  if (lastDismissedAt === null) {
    return true;
  }
  return nowMs - lastDismissedAt >= DISMISS_COOLDOWN_MS;
}

/**
 * Read the last-dismissed timestamp from AsyncStorage.
 * Returns null if not set or if the read fails (disk full).
 */
export async function getLastDismissedAt(): Promise<number | null> {
  try {
    const raw = await AsyncStorage.getItem(LAST_DISMISSED_KEY);
    if (raw === null) {
      return null;
    }
    const parsed = parseInt(raw, 10);
    return isNaN(parsed) ? null : parsed;
  } catch (err) {
    captureException(err, {
      channel: "warning",
      area: "disk-space",
      operation: "read-last-dismissed",
    });
    return null;
  }
}

/**
 * Persist the current time as the last-dismissed timestamp.
 * Degrades gracefully if the disk is full.
 */
export async function persistDismissedAt(nowMs: number): Promise<void> {
  try {
    await AsyncStorage.setItem(LAST_DISMISSED_KEY, String(nowMs));
  } catch (err) {
    captureException(err, {
      channel: "warning",
      area: "disk-space",
      operation: "persist-dismissed-at",
    });
    // Degrade gracefully — the user dismissed, we just won't persist it.
  }
}

/**
 * Check whether we should show the low-storage warning.
 * Returns false on any error (fail-safe).
 */
export async function shouldShowLowStorageWarning(): Promise<boolean> {
  try {
    const available = Paths.availableDiskSpace;
    const total = Paths.totalDiskSpace;

    if (!isStorageCriticallyLow(available, total)) {
      return false;
    }

    const lastDismissedAt = await getLastDismissedAt();
    return isCooldownElapsed(lastDismissedAt, Date.now());
  } catch (err) {
    captureException(err, {
      channel: "warning",
      area: "disk-space",
      operation: "check-low-storage",
    });
    return false;
  }
}

/** Prevents multiple simultaneous low-storage alerts from stacking. */
let _alertVisible = false;

/**
 * Show a React Native Alert warning the user that storage is critically low.
 * Guards against concurrent triggers: if an alert is already on screen the
 * call is a no-op. Persists the dismiss cooldown whichever button is pressed.
 */
export function showLowStorageAlert(): void {
  if (_alertVisible) {
    return;
  }
  _alertVisible = true;

  const dismiss = () => {
    _alertVisible = false;
    persistDismissedAt(Date.now()).catch(() => {
      // persistDismissedAt already captures the exception internally.
    });
  };

  Alert.alert(
    "Low Storage",
    "Your device is very low on storage. Please free up some space to ensure Lissen functions correctly.",
    [
      {
        text: "Open Settings",
        onPress: () => {
          dismiss();
          if (Platform.OS !== "web" && Linking.openSettings) {
            Linking.openSettings().catch(() => {
              // Silently ignore — the OS may not support this on all platforms.
            });
          }
        },
      },
      {
        text: "Dismiss",
        style: "cancel",
        onPress: () => {
          dismiss();
        },
      },
    ],
  );
}

/**
 * Determine whether a storage write error is a disk-full error.
 * Matches POSIX error code 28 (ENOSPC) on iOS/Android.
 */
export function isDiskFullError(err: unknown): boolean {
  if (err instanceof Error) {
    // iOS: NSPOSIXErrorDomain Code=28 / POSIX ENOSPC
    if (/code.*28|enospc|no space left/i.test(err.message)) {
      return true;
    }
    // Some Android/Node variants expose a code property
    const asAny = err as { code?: string | number };
    if (asAny.code === "ENOSPC" || asAny.code === 28) {
      return true;
    }
  }
  return false;
}
